<script setup>
import EmailDialog from "@/components/dialogs/EmailDialog.vue";
import PlansModalBase from "@/components/users/profile/subscription_form/modal/plans/PlansModalBase.vue";
import SingleDocumentPlanCard from "@/components/users/profile/subscription_form/modal/plans/SingleDocumentPlanCard.vue";
import SubscriptionsPlanCard from "@/components/users/profile/subscription_form/modal/plans/SubscriptionsPlanCard.vue";
import PlanCardSkeleton from "@/components/users/profile/subscription_form/modal/plans/PlanCardSkeleton.vue";
import SingleDocumentPlanCardSkeleton from "@/components/users/profile/subscription_form/modal/plans/SingleDocumentPlanCardSkeleton.vue";

import store from "@/store/store";
import axios from "axios";
import { computed, inject, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "vue-toastification";

const { isDialogOpen, templateId, documentId, showCloseButton } = defineProps([
  "isDialogOpen",
  "templateId",
  "documentId",
  "showCloseButton",
]);
const emit = defineEmits(["closeDialog"]);
const isPaymentDialogOpen = inject("isPaymentDialogOpen");

const { t, tm } = useI18n();

const planBenefits = computed(() => tm("billing.plans_benefits2"));

const isTemporaryUser = computed(() => store.state.auth.user.isTemporary);
const plans = ref([]);
const isOpen = ref(isDialogOpen);
const isLoading = ref(false);

// Filter out plans with missing essential data
const validPlans = computed(() => {
  if (!plans.value || !Array.isArray(plans.value)) return [];

  return plans.value.filter(plan => {
    return plan &&
           plan.id &&
           plan.name &&
           plan.currency_symbol &&
           (plan.unit_price !== undefined && plan.unit_price !== null);
  });
});

const oneTimePlan = computed(() => validPlans.value.find((plan) => plan.type === "one_time"));
const recurringSubscriptionPlans = computed(() =>
  validPlans.value
    .filter((plan) => plan.type === "recurring")
    .sort((a, b) => (b.price || 0) - (a.price || 0))
);

const oneTimeSubscriptionPlan = computed(() =>
  validPlans.value
    .filter((plan) => plan.type === "one_time_payment")
    .sort((a, b) => (b.price || 0) - (a.price || 0))
);

const getPaymentRequiredInfo = async () => {
  try {
    isLoading.value = true;
    const response = await axios.get(`/api/templates/${templateId}/payment-info`);
    plans.value = response.data;
  } catch (error) {
    useToast().error(t("notifications.default_error"));
  } finally {
    isLoading.value = false;
  }
};

const createSinglePayment = async (payment) => {
  // Construct the success URL we want to redirect to after payment
  const successUrl = `${window.location.origin}/document-questions?template=${templateId}&documentId=${documentId}`;
  // Use current URL as cancel URL with a parameter to indicate cancelled payment
  const cancelUrl = `${window.location.href}${window.location.href.includes('?') ? '&' : '?'}payment_cancelled=true`;

  await axios
    .post(`/api/user/payments/create/${payment.provider}`, {
      price_id: payment.price,
      product_id: templateId,
      document_id: documentId,
      success_url: successUrl,
      cancelUrl: cancelUrl,
    })
    .then((res) => {
      emit("closeDialog");
      window.location.replace(res.data.data.checkout_url);
    })
    .catch(() => {
      useToast().error(t("notifications.default_error"));
    });
};

const handleCloseModal = () => {
  isOpen.value = false;
  emit("closeDialog");
};

watch(
  () => isDialogOpen,
  (value) => {
    isOpen.value = value;
  },
);

watch(
  () => isPaymentDialogOpen.value,
  (value) => {
    isOpen.value = value;
  },
);

onMounted(async () => {
  await getPaymentRequiredInfo();
});
</script>

<template>
  <EmailDialog v-if="isTemporaryUser" :isDialogOpen="isOpen" @closeDialog="handleCloseModal" />
  <PlansModalBase
    v-else
    class="SubscriptionOrSinglePaymentRequiredDialog"
    :open="isOpen"
    :show-close-button="true"
    @close="handleCloseModal"
  >
    <!-- Loading skeletons -->
    <template v-if="isLoading">
      <PlanCardSkeleton />
      <PlanCardSkeleton />
      <SingleDocumentPlanCardSkeleton />
    </template>

    <!-- Actual plan cards -->
    <template v-else>
      <SubscriptionsPlanCard
        v-if="recurringSubscriptionPlans.length > 0"
        :plans="recurringSubscriptionPlans"
        :plans-benefits="planBenefits.recurring"
        :document-id="documentId"
        :template-id="templateId"
        @closeModal="handleCloseModal"
      />
      <SubscriptionsPlanCard
        v-if="oneTimeSubscriptionPlan.length > 0"
        :plans="oneTimeSubscriptionPlan"
        :plans-benefits="planBenefits.one_time_payment"
        :document-id="documentId"
        :template-id="templateId"
        @closeModal="handleCloseModal"
      />
      <SingleDocumentPlanCard
        v-if="oneTimePlan"
        :plan="oneTimePlan"
        :plans-benefits="planBenefits.one_time"
        @closeModal="handleCloseModal"
        @create-single-payment="createSinglePayment"
      />
    </template>
  </PlansModalBase>
</template>
<style>
.SubscriptionOrSinglePaymentRequiredDialog .background-container {
  background: none;
}
.SubscriptionOrSinglePaymentRequiredDialog .v-card {
  margin-bottom: 50px !important;

  @media (max-width: 768px) {
    margin-bottom: 15px !important;
  }
}
</style>
